import { Ability } from '@casl/ability';
import { Actions, MultiSelectOption, Subjects } from '@dispatch/shared/types';
import {
  CheckBoxField,
  Section,
  SelectField,
  TextField,
} from '@dispatch/web/ui';
import { Grid, Stack, Tooltip } from '@mui/material';
import { Control, FieldErrors, FieldValues } from 'react-hook-form';
import { ResourceContext } from '../../ResourcesDetails';
import React from 'react';

export type GeneralInformationProps = {
  control: Control<FieldValues>;
  permissions: Ability<[Actions, Subjects]> | undefined;
  errors: FieldErrors;
  efficiencies: MultiSelectOption[];
  isReadOnly?: boolean;
};

export const GeneralInformation = ({
  control,
  permissions,
  errors,
  efficiencies,
  isReadOnly = false,
}: GeneralInformationProps) => {
  const hasUpsertPermissions = permissions?.can?.('upsert', 'resource');
  const hasCWSUpsertPermissions = permissions?.can?.('upsert', 'cws');
  const { resourceWatch } = React.useContext(ResourceContext);
  const isWorkTeam = resourceWatch?.('generalInformation.isWorkTeam');

  return (
    <Section title="General Information">
      <Stack>
        <div
          style={{
            padding: '10px 24px',
          }}
        >
          <Section>
            <Grid container alignItems="center" spacing={2}>
              <Grid size={3}>
                <TextField
                  id="name"
                  name="generalInformation.name"
                  label="Name"
                  control={control}
                  inputProps={{
                    readOnly: !hasUpsertPermissions,
                  }}
                  errors={errors}
                  required
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid size={3}>
                <TextField
                  id="externalResourceId"
                  name="generalInformation.externalResourceId"
                  label="Resource ID"
                  type="text"
                  control={control}
                  inputProps={{
                    readOnly: !hasUpsertPermissions,
                  }}
                  errors={errors}
                  required
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid size={3}>
                <TextField
                  id="commuteTimeInMin"
                  name="generalInformation.commuteTimeInMin"
                  label="Allowable Commute Time (minutes)"
                  type="number"
                  control={control}
                  inputProps={{
                    readOnly: !hasUpsertPermissions,
                  }}
                  errors={errors}
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid justifyContent="space-between" size={3}>
                <SelectField
                  id="efficiency"
                  name="generalInformation.efficiency"
                  label="Efficiency"
                  control={control}
                  readOnly={!hasUpsertPermissions}
                  errors={errors}
                  options={efficiencies}
                  // this is needed to display values that are not in dropdown
                  overrideOptionsLabel={(option: unknown) => {
                    const multiSelOption = option as MultiSelectOption;

                    if (multiSelOption?.title) {
                      return multiSelOption.title;
                    } else {
                      const result = efficiencies.find(
                        ({ value: findValue }) => findValue === option
                      );
                      if (result) {
                        return result?.title;
                      }
                    }

                    return `${option}%`;
                  }}
                  disabled={isReadOnly}
                />
              </Grid>
            </Grid>
            <Grid container alignItems="center" spacing={2}>
              <Grid size={3}>
                <TextField
                  id="email"
                  name="generalInformation.email"
                  label="Email"
                  type="text"
                  control={control}
                  inputProps={{
                    readOnly: !hasUpsertPermissions,
                  }}
                  errors={errors}
                  required
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid size={3}>
                <TextField
                  id="phone"
                  name="generalInformation.phone"
                  label="Phone Number"
                  type="text"
                  control={control}
                  inputProps={{
                    readOnly: !hasUpsertPermissions,
                  }}
                  errors={errors}
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid size={3}>
                <TextField
                  id="wipLimit"
                  name="generalInformation.wipLimit"
                  label="WIP Limit"
                  type="number"
                  control={control}
                  inputProps={{
                    readOnly: !hasUpsertPermissions,
                  }}
                  errors={errors}
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid size={3}>
                <TextField
                  id="description"
                  name="generalInformation.description"
                  label="Description"
                  control={control}
                  inputProps={{
                    readOnly: !hasUpsertPermissions,
                  }}
                  errors={errors}
                  disabled={isReadOnly}
                />
              </Grid>
              {hasCWSUpsertPermissions && (
                <Grid size={3}>
                  <TextField
                    id="cwsUserId"
                    name="generalInformation.cwsUserId"
                    label="CWS User ID"
                    control={control}
                    errors={errors}
                    disabled={isReadOnly}
                  />
                </Grid>
              )}
              <Grid size={12}></Grid>
            </Grid>
            <Grid container alignItems="center" spacing={2}>
              <Grid size={3}>
                <CheckBoxField
                  sx={{
                    checkbox: { padding: 0, margin: 0 },
                    label: { padding: 0, margin: 0 },
                  }}
                  id="generalInformation.isWorkTeam"
                  name="generalInformation.isWorkTeam"
                  label="Work Team"
                  control={control}
                  disabled={isReadOnly}
                />
              </Grid>
              <Grid size={6}>
                {isWorkTeam && (
                  <Tooltip
                    title={
                      <p
                        style={{ fontSize: '15px', margin: '0', padding: '0' }}
                      >
                        Tasks assigned to this resource will be locked
                        immediately
                      </p>
                    }
                  >
                    <div>
                      <CheckBoxField
                        sx={{
                          checkbox: { padding: 0, margin: 0 },
                          label: { padding: 0, margin: 0 },
                        }}
                        id="generalInformation.lockImmediately"
                        name="generalInformation.lockImmediately"
                        label="Lock Tasks Immediately"
                        control={control}
                        disabled={isReadOnly}
                      />
                    </div>
                  </Tooltip>
                )}
              </Grid>
            </Grid>
            <Grid container alignItems="center" spacing={2}>
              <Grid size={3}>
                <CheckBoxField
                  sx={{
                    checkbox: { padding: 0, margin: 0 },
                    label: { padding: 0, margin: 0 },
                  }}
                  id="generalInformation.isAlwaysAvailable"
                  name="generalInformation.isAlwaysAvailable"
                  label="Always Available"
                  control={control}
                  disabled={isReadOnly}
                />
              </Grid>
            </Grid>
          </Section>
        </div>
      </Stack>
    </Section>
  );
};
